import React, { useEffect, useState } from 'react';
import { Button, Form, Input, Spin, Divider, List, Tag, Space, Popconfirm } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import * as partnerApi from '../../modules/partners/api';

const { TextArea } = Input;

const PartnerForm = ({ handleSubmit, action, partner, handleCancel, isFetching }) => {
  const [form] = Form.useForm();
  const { t } = useTranslation('partners');
  const { t: tc } = useTranslation('common');
  const [itemTypes, setItemTypes] = useState([]);
  const [newItemTypeName, setNewItemTypeName] = useState('');
  const [itemTypesLoading, setItemTypesLoading] = useState(false);

  const onFinishForm = (values) => {
    handleSubmit && handleSubmit(values);
  };

  useEffect(() => {
    if (action === 'new') {
      form.setFieldsValue({
        name: null,
        code: null,
        contactEmail: null,
        contactPhone: null,
        address: null,
        notes: null,
      });
      setItemTypes([]);
    } else if (partner) {
      form.setFieldsValue({
        name: partner.name,
        code: partner.code,
        contactEmail: partner.contactEmail,
        contactPhone: partner.contactPhone,
        address: partner.address,
        notes: partner.notes,
      });
      // Load partner item types for existing partner
      loadItemTypes(partner.id);
    }
  }, [action, partner]);

  const loadItemTypes = async (partnerId) => {
    setItemTypesLoading(true);
    const { response } = await partnerApi.getPartnerItemTypes(partnerId);
    if (response) {
      setItemTypes(response);
    }
    setItemTypesLoading(false);
  };

  const handleAddItemType = async () => {
    if (!newItemTypeName.trim() || !partner?.id) return;
    setItemTypesLoading(true);
    const { response } = await partnerApi.createPartnerItemType(partner.id, {
      name: newItemTypeName.trim(),
    });
    if (response) {
      setItemTypes([...itemTypes, response]);
      setNewItemTypeName('');
    }
    setItemTypesLoading(false);
  };

  const handleRemoveItemType = async (id) => {
    setItemTypesLoading(true);
    const { response } = await partnerApi.removePartnerItemType(id);
    if (response) {
      setItemTypes(itemTypes.filter((t) => t.id !== id));
    }
    setItemTypesLoading(false);
  };

  return (
    <Spin spinning={isFetching}>
      <div className="RiderForm">
        <div className="FormHeader"></div>
        <div className="FormContent">
          <Form
            form={form}
            scrollToFirstError
            layout="vertical"
            requiredMark={false}
            colon={false}
            onFinish={onFinishForm}
          >
            <Form.Item label={t('partnerName')} name="name" rules={[{ required: true, message: tc('required') }]}>
              <Input placeholder="e.g. Migros, Sterilease" />
            </Form.Item>

            <Form.Item label={tc('code')} name="code">
              <Input placeholder={t('autoGeneratedFromName')} disabled={action === 'edit'} />
            </Form.Item>

            <Form.Item
              label={t('contactEmail')}
              name="contactEmail"
              rules={[{ type: 'email', message: tc('invalidEmail') }]}
            >
              <Input placeholder="ops@partner.com" />
            </Form.Item>

            <Form.Item label={t('contactPhone')} name="contactPhone">
              <Input placeholder="+41..." />
            </Form.Item>

            <Form.Item label={t('address')} name="address">
              <Input placeholder={t('partnerAddress')} />
            </Form.Item>

            <Form.Item label={t('notes')} name="notes">
              <TextArea rows={3} placeholder={t('internalNotes')} />
            </Form.Item>
          </Form>

          {/* Returnable Item Types — only in edit mode */}
          {action === 'edit' && partner?.id && (
            <>
              <Divider />
              <div style={{ marginBottom: 8 }}>
                <strong>{t('returnableItemTypes')}</strong>
                <span style={{ color: '#9E9E9E', marginLeft: 8, fontSize: 12 }}>{t('returnableItemTypesHint')}</span>
              </div>

              <Spin spinning={itemTypesLoading}>
                <List
                  size="small"
                  bordered
                  dataSource={itemTypes}
                  locale={{ emptyText: t('noItemTypes') }}
                  renderItem={(item) => (
                    <List.Item
                      actions={[
                        <Tag key="active" color={item.isActive !== false ? 'green' : 'default'}>
                          {item.isActive !== false ? t('active') : t('inactive')}
                        </Tag>,
                        <Popconfirm
                          key="delete"
                          title={t('removeItemType')}
                          onConfirm={() => handleRemoveItemType(item.id)}
                          okText={tc('yes')}
                          cancelText={tc('no')}
                        >
                          <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                        </Popconfirm>,
                      ]}
                    >
                      <List.Item.Meta
                        title={item.name}
                        description={item.codePrefix ? `Code prefix: ${item.codePrefix}` : null}
                      />
                      {item._count?.collectedItems > 0 && <Tag>{item._count.collectedItems} collected</Tag>}
                    </List.Item>
                  )}
                />

                <Space style={{ marginTop: 12, width: '100%' }}>
                  <Input
                    value={newItemTypeName}
                    onChange={(e) => setNewItemTypeName(e.target.value)}
                    placeholder={t('newItemTypeName')}
                    onPressEnter={handleAddItemType}
                    style={{ width: 300 }}
                  />
                  <Button
                    type="dashed"
                    icon={<PlusOutlined />}
                    onClick={handleAddItemType}
                    disabled={!newItemTypeName.trim()}
                  >
                    {tc('add')}
                  </Button>
                </Space>
              </Spin>
            </>
          )}
        </div>

        <div className="FormFooter">
          <Button key="cancel" onClick={handleCancel}>
            {tc('cancel')}
          </Button>
          <Button type="primary" block size="large" onClick={() => form.submit()}>
            {action === 'new' ? t('createPartner') : t('updatePartner')}
          </Button>
        </div>
      </div>
    </Spin>
  );
};

export default PartnerForm;
